import { Flex, HStack, Stack, Text, VStack } from '@chakra-ui/react';
import GoogleMapReact from 'google-map-react';
import Image from 'next/image';
import Link from 'next/link';
import React, { Children, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useT from '../../../../lib/t';
import { CORPORATE_SEARCH_KEYWORD, customMapStyle } from '@/lib/constant';
import classNames from 'classnames';
import { CountryEnum, FindDirectionTimeEntity, TravelMode } from '@/swagger';
import ConditionalFragment from '@/components/common/ConditionalFragment';
import Icon from '@/public/icon';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import dynamic from 'next/dynamic';
import { distanceConvertor } from '@/utils/distance-convertor';

const AddCustomizeTransportation = dynamic(() => import('@/src/components/UI/HouseDetail/AddCustomizeTransportation'), {
  ssr: false,
});

interface ItemDetail {
  distance?: string;
  title?: string;
  walkTime?: string;
  driveTime?: string;
  name?: string;
  type:
    | 'bus'
    | 'grocery'
    | 'gym'
    | 'home'
    | 'hospital'
    | 'park'
    | 'restaurant'
    | 'school'
    | 'subway'
    | 'corporate'
    | 'corporate_campus'
    | 'corporate_office'
    | 'amazon office'
    | 'microsoft office'
    | 'google office'
    | 'facebook office'
    | 'boeing office'
    | 'tableau office'
    | 'expedia Group office'
    | 'sap Concur office'
    | 'redfin office'
    | 'starbucks office';
  location?: {
    lat: number;
    lng: number;
    type: string;
  };
}

interface Env {
  hospital: ItemDetail[];
  recreation: ItemDetail[];
  school: ItemDetail[];
  supermarket: ItemDetail[];
  traffic: ItemDetail[];
  corporate: ItemDetail[];
}

export type EnvKey = 'hospital' | 'traffic' | 'recreation' | 'school' | 'supermarket' | 'corporate';

enum TransportationWay {
  'default' = 'default',
  'walk' = 'walk',
  'car' = 'car',
}

export const HomeIconMarker = () => (
  <div className={' w-3xl h-3xl'}>
    <Image src={'/markers-home-selected.png'} width={24} height={24} objectFit={'contain'} layout={'responsive'} />
  </div>
);
export const markerImages: {
  [key: string]: string;
} = {
  bus_station_selected: '/marker/markers-bus-selected.png',
  bus_station_unselected: '/marker/markers-bus-unselected.png',
  bus_selected: '/marker/markers-bus-selected.png',
  bus_unselected: '/marker/markers-bus-unselected.png',
  supermarket_selected: '/marker/markers-grocery-selected.png',
  supermarket_unselected: '/marker/markers-grocery-unselected.png',
  home_selected: '/marker/markers-home-selected.png',
  home_unselected: '/marker/markers-home-selected.png',
  attraction_selected: '/marker/markers-attraction-selected.png',
  attraction_unselected: '/marker/markers-attraction-unselected.png',
  artGallery_selected: '/marker/markers-artGallery-selected.png',
  artGallery_unselected: '/marker/markers-artGallery-unselected.png',
  movieTheater_selected: '/marker/markers-movieTheater-selected.png',
  movieTheater_unselected: '/marker/markers-movieTheater-unselected.png',
  museum_selected: '/marker/markers-museum-selected.png',
  museum_unselected: '/marker/markers-museum-unselected.png',
  library_selected: '/marker/markers-library-selected.png',
  library_unselected: '/marker/markers-library-unselected.png',
  grocery_selected: '/marker/markers-grocery-selected.png',
  grocery_unselected: '/marker/markers-grocery-unselected.png',
  gym_selected: '/marker/markers-gym-selected.png',
  gym_unselected: '/marker/markers-gym-unselected.png',
  subway_selected: '/marker/markers-subway-selected.png',
  subway_unselected: '/marker/markers-subway-unselected.png',
  subway_station_selected: '/marker/markers-subway-selected.png',
  subway_station_unselected: '/marker/markers-subway-unselected.png',
  hospital_selected: '/marker/markers-hospital-selected.png',
  hospital_unselected: '/marker/markers-hospital-unselected.png',
  park_selected: '/marker/markers-park-selected.png',
  park_unselected: '/marker/markers-park-unselected.png',
  restaurant_selected: '/marker/markers-restaurant-selected.png',
  restaurant_unselected: '/marker/markers-restaurant-unselected.png',
  school_selected: '/marker/markers-school-selected.png',
  school_unselected: '/marker/markers-school-unselected.png',
  corporate_selected: '/marker/markers-company-selected.png',
  corporate_unselected: '/marker/markers-company-unselected.png',
};

const splitValueAndUnits = (set: string = ''): { value: number; unit: string } => {
  const regex = /(\d+\.?\d{0,})\s{0,}(.*)?/;
  const [, value = '0', unit] = regex.exec(set) ?? [];

  return { value: parseFloat(value), unit: unit?.toLocaleLowerCase() };
};

export const CustomMarker = ({
  type,
  detail,
  selected,
  onClick,
}: {
  type: string;
  detail: any;
  selected: boolean;
  onClick: () => void;
  lat?: number;
  lng?: number;
}) => {
  if (!markerImages[type + '_selected']) return <div className={' w-3xl h-3xl'}></div>;
  return (
    <>
      {selected ? (
        <Flex
          w={{ base: '40vw', sm: '20vw' }}
          justify={'center'}
          flex={1}
          align={'start'}
          direction={'column'}
          position={'absolute'}
          px={3}
          py={2}
          bottom={0}
          left={'-10vw'}
          bg={'white'}
        >
          <Text color={'gray.900'} pb={1}>
            {detail.name!}
          </Text>
          <HStack align={'center'} justify={'space-between'} w={'100%'}>
            <Text color={'gray.700'}>{detail.distance}</Text>
            <Flex>
              <Image src={'/directions_walk_black_24dp.svg'} width={16} height={16} objectFit={'contain'} />
              <p className=' leading-normal tracking-wide  text-right'>{detail.walkTime}</p>
            </Flex>
          </HStack>
          <a href={`https://www.google.com/maps/place/${detail.location.lat},${detail.location.lng}`}>
            <Text decoration={'underline'} color={'gray.700'}>
              查看 Google Map
            </Text>
          </a>
        </Flex>
      ) : null}
      <div onClick={onClick} className={' w-3xl h-3xl'}>
        <Image
          src={markerImages[type + (selected ? '_selected' : '_unselected')]}
          width={24}
          height={24}
          objectFit={'contain'}
          layout={'responsive'}
        />
      </div>
    </>
  );
};

const sortTrafficEnvInDistance = (data: ItemDetail[]): Array<ItemDetail> =>
  data.sort((a, b) => (Number(a.distance ?? '0') > Number(b.distance ?? '0') ? 1 : -1));
const MapViewSection = ({
  center,
  items,
  itemEnvTitle,
  sid,
  country,
}: {
  center: { lat: number; lng: number };
  items: Env;
  sid: string;
  itemEnvTitle?: MutableRefObject<HTMLDivElement | null>;
  country: CountryEnum;
}) => {
  const [selectedPill, setSelectedPill] = useState<EnvKey>('traffic');
  const [selectedMarker, setSelectedMarker] = useState<string | null>(null);
  const [transportationWay, setTransportationWay] = useState<TransportationWay>(TransportationWay.default);
  const [selfDefineTransportation, setSelfDefineTransportation] = useState<Array<FindDirectionTimeEntity>>([]);
  const [isStartSelfDefine, setIsStartSelfDefine] = useState<boolean>(false);

  const onClick = useCallback(
    (id: string) => {
      setSelectedMarker(id);
    },
    [setSelectedMarker]
  );
  useEffect(() => {
    setSelectedMarker(null);
  }, [selectedPill]);

  const triggerStartSelfDefine = useCallback(() => {
    setIsStartSelfDefine((prev) => !prev);
  }, []);

  const onAfterCalculating = useCallback((data: FindDirectionTimeEntity) => {
    setSelfDefineTransportation((prev) => prev.concat(data));
    setIsStartSelfDefine(false);
  }, []);

  const removeSelfDefine = useCallback((index: number) => {
    setSelfDefineTransportation((prev) => {
      return prev.slice(0, index).concat(prev.slice(index + 1));
    });
  }, []);

  const displayItems = useMemo(() => sortTrafficEnvInDistance(items[selectedPill] || []), [items, selectedPill]);

  return (
    <div ref={itemEnvTitle} className={'flex flex-col  w-full pt-9 '}>
      <section
        id='surroundings'
        className='hidden sm:block text-lg leading-loose font-medium text-dark-grey uppercase mb-2'
      >
        周邊環境
      </section>
      <div className={'mb-6'}>
        <VStack spacing={2.5} className={'mx-4 sm:mx-0'}>
          {selfDefineTransportation.map(({ name, time, type }, index) => (
            <div
              key={`${name}-${index}`}
              className={classNames('flex items-center', 'p-3', 'w-full', 'bg-gray100', 'overflow-hidden')}
            >
              <div className={'mr-3 pr-3 border-r border-solid border-gray900'}>
                <ConditionalFragment condition={type === TravelMode.Driving}>
                  <Icon.DirectionCarIcon />
                </ConditionalFragment>
                <ConditionalFragment condition={type === TravelMode.Walking}>
                  <Icon.DirectionWalkIcon />
                </ConditionalFragment>
                <ConditionalFragment condition={type === TravelMode.Transit}>
                  <Icon.DirectionBusIcon />
                </ConditionalFragment>
                <ConditionalFragment condition={type === TravelMode.Bicycling}>
                  <Icon.DirectionBikeIcon />
                </ConditionalFragment>
              </div>
              <p className={'flex items-center flex-1 mr-2 overflow-hidden'}>
                <span className={'font-semibold'}>至</span>
                <span className={'mx-3 truncate'}>{name}</span>
                <span className={'font-semibold flex-shrink-0'}>需 {time}</span>
              </p>
              <button className={'w-6 h-6 flex-shrink-0'}>
                <CloseIcon fontSize={'12px'} onClick={() => removeSelfDefine(index)} />
              </button>
            </div>
          ))}
        </VStack>
        <button
          onClick={triggerStartSelfDefine}
          type={'button'}
          className={'flex items-center py-4 px-4 sm:px-0 w-fit'}
        >
          <ConditionalFragment condition={!isStartSelfDefine}>
            <AddIcon className={'mr-2'} />
          </ConditionalFragment>
          <ConditionalFragment condition={isStartSelfDefine}>
            <CloseIcon className={'mr-2'} />
          </ConditionalFragment>
          新增你的通勤地點
        </button>
        <AddCustomizeTransportation
          country={country}
          coordinates={center}
          onAfterCalculating={onAfterCalculating}
          isStartSelfDefine={isStartSelfDefine}
          triggerStartSelfDefine={triggerStartSelfDefine}
        />
      </div>
      <div className={'w-full h-96 relative'}>
        <GoogleMapReact
          options={() => ({
            draggable: false,
            fullscreenControl: false,
            zoomControl: false,
            styles: customMapStyle,
            panControl: false,
            mapTypeControl: false,
            scrollwheel: false,
          })}
          bootstrapURLKeys={{ key: process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY! }}
          defaultCenter={center}
          defaultZoom={13}
        >
          <HomeIconMarker />
          {displayItems.map((item, index) => {
            let type = item.type;
            const key = `${item.name}_${item.title}_${item.type}`;

            if (CORPORATE_SEARCH_KEYWORD.includes(type)) type = 'corporate';

            return (
              <CustomMarker
                detail={item}
                onClick={() => onClick(key)}
                selected={selectedMarker === key}
                {...item.location}
                key={index}
                type={type}
              />
            );
          })}
        </GoogleMapReact>
        <Link href={`/buy/${sid}/map`}>
          <a>
            <Stack position={'absolute'} bottom={'12px'} right={'12px'} w={{ base: '35%', sm: '15%' }}>
              <Image width={360} height={270} layout={'responsive'} src={'/map.png'} />
            </Stack>
          </a>
        </Link>
      </div>
      <div className={'flex flex-col sm:flex-row sm:items-center px-4 sm:px-0 pt-4 sm:pt-0'}>
        <MapNavigationPill country={country} selectedPill={selectedPill} onSelect={(value) => setSelectedPill(value)} />
        <Flex className={'sm:ml-auto w-full sm:w-auto border border-solid border-gray900 divide-x divide-gray900'}>
          {Object.keys(TransportationWay).map((way) => (
            <Flex
              as={'button'}
              onClick={() => setTransportationWay(way as TransportationWay)}
              alignItems={'center'}
              justifyContent={'center'}
              key={way}
              className={classNames('flex-1 sm:flex-auto sm:w-20 h-8', {
                'bg-gray900': way === transportationWay,
                'text-white': way === transportationWay,
              })}
            >
              <ConditionalFragment condition={way === TransportationWay.default}>預設交通</ConditionalFragment>
              <ConditionalFragment condition={way === TransportationWay.walk}>
                <Icon.DirectionWalkIcon />
              </ConditionalFragment>
              <ConditionalFragment condition={way === TransportationWay.car}>
                <Icon.DirectionCarIcon />
              </ConditionalFragment>
            </Flex>
          ))}
        </Flex>
      </div>
      <Flex direction={'column'} w={'100%'}>
        {items[selectedPill]?.slice(0, 3)?.map((res, index) => {
          const key = `${res.name}_${res.title}_${res.type}`;

          return (
            <EnvironmentItem key={index} onClick={() => onClick(key)} {...res} transportationWay={transportationWay} />
          );
        })}
        <CollapsibleData dependency={selectedPill}>
          {items[selectedPill]?.slice(4)?.map((res, idx) => {
            const key = `${res.name}_${res.title}_${res.type}`;

            return (
              <EnvironmentItem
                key={'mapItem' + idx}
                {...res}
                onClick={() => onClick(key)}
                transportationWay={transportationWay}
              />
            );
          })}
        </CollapsibleData>
        <p className={'p-4 text-gray500 text-sm'}>預設交通：步行超過 15 分鐘，以車程顯示</p>
      </Flex>
      <div className={'h-9 w-full'}></div>
      <Flex px={4}>
        <div className={'h-px w-full bg-gray300 '} />
      </Flex>
    </div>
  );
};

const EnvironmentItem: React.FC<ItemDetail & { onClick: () => void; transportationWay: TransportationWay }> = (res) => {
  const tu = useT('share.unit');

  const { value: walkTime, unit: walkTimeUnit } = splitValueAndUnits(res.walkTime);
  const { value: driveTime, unit: driveTimeUnit } = splitValueAndUnits(`${res.driveTime ?? `${walkTime / 8} 分鐘`}`);
  let walkTimeUnitI18Key = walkTimeUnit === '分鐘' ? 'min' : '';
  let driveTimeUnitI18Key = driveTimeUnit === '分鐘' ? 'min' : '';
  const convertedDistance = distanceConvertor(res.distance ?? '');

  const transportationWay =
    res.transportationWay === TransportationWay.default && Math.round(walkTime) < 15
      ? TransportationWay.walk
      : res.transportationWay === TransportationWay.walk
      ? TransportationWay.walk
      : TransportationWay.car;

  return (
    <Flex w={'100%'} direction={'row'} py={3} px={3} justify={'space-between'} align={'center'} onClick={res.onClick}>
      <Flex align={'center'} direction={'row'} flex={{ base: 4.5, sm: 5, md: 6 }}>
        <p className=' leading-normal tracking-wide text-dark-grey uppercase '>{res.name}</p>
      </Flex>
      <Flex align={'center'} justify={'center'} direction={'row'} flex={{ base: 2, sm: 2.5, md: 2 }}>
        <p className=' leading-normal tracking-wide text-dark-grey uppercase'>
          {convertedDistance.value} {tu(convertedDistance.unit)}
        </p>
      </Flex>
      <Flex flex={{ base: 3.5, sm: 2.5, md: 2 }} justify={'flex-end'} align={'center'} direction={'row'}>
        <ConditionalFragment condition={transportationWay === TransportationWay.walk}>
          <Image src={'/directions_walk_black_24dp.svg'} width={24} height={24} objectFit={'contain'} />
          <p className=' leading-normal tracking-wide  text-right'>
            {Math.round(walkTime)} {tu(walkTimeUnitI18Key)}
          </p>
        </ConditionalFragment>
        <ConditionalFragment condition={transportationWay === TransportationWay.car}>
          <Image src={'/directions_car_black_24dp.svg'} width={24} height={24} objectFit={'contain'} />
          <p className='ml-1 leading-normal tracking-wide  text-right'>
            {Math.max(Math.round(driveTime), 1)} {tu(driveTimeUnitI18Key)}
          </p>
        </ConditionalFragment>
      </Flex>
    </Flex>
  );
};

const CollapsibleData: React.FC<{ dependency: any }> = ({ children, dependency }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const collapsibleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsExpanded(false);
  }, [dependency]);

  const height = useMemo(() => {
    if (isExpanded) return collapsibleRef.current?.scrollHeight || 0;
    else return 0;
  }, [isExpanded]);

  if (Children.count(children) === 0) return null;

  return (
    <div>
      <div
        ref={collapsibleRef}
        style={{ height, transition: 'height', transitionDuration: '300ms' }}
        className={classNames('overflow-hidden')}
      >
        {children}
      </div>
      <div
        className={classNames(
          'flex items-center justify-center',
          'py-2 px-3',
          'w-full',
          'text-gray900',
          'cursor-pointer'
        )}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <span>{isExpanded ? '隱藏更多交通資訊' : '更多交通資訊'}</span>
        <Stack
          width={6}
          height={6}
          margin={0}
          marginTop={0}
          transition={'transform 300ms'}
          transform={!isExpanded ? 'rotate(180deg)' : ''}
          cursor={'pointer'}
        >
          <Image src={'/chevron-up.svg'} layout={'responsive'} width={24} height={24} />
        </Stack>
      </div>
    </div>
  );
};

export const TrafficOrder: EnvKey[] = ['traffic', 'school', 'supermarket', 'recreation', 'hospital', 'corporate'];

const Trans = {
  hospital: '醫院',
  recreation: '娛樂',
  traffic: '交通',
  school: '學校',
  supermarket: '超市',
  corporate: '公司',
};

interface MapNavigationPillProps {
  selectedPill: string;
  onSelect: (key: EnvKey) => void;
  country: CountryEnum;
}

export function MapNavigationPill({ selectedPill, onSelect, country }: MapNavigationPillProps) {
  const pills = TrafficOrder.map((key) => ({
    key,
    /* @ts-ignore */
    title: Trans[key],
    country: CountryEnum,
  })).filter((pill) => (country === 'TW' ? pill.key !== 'corporate' : true));

  return (
    <HStack
      columns={pills.length}
      w={{ w: '100%' }}
      align={'center'}
      my={2}
      direction={'row'}
      className={'overflow-scroll order-last sm:order-first'}
    >
      {pills.map(({ key, title }) => {
        if (country === 'TW' && key === 'corporate') return null;
        return (
          <div
            onClick={() => onSelect(key)}
            key={key}
            className={`cursor-pointer flex-1 sm:flex-auto py-1 sm:px-4 rounded-2xl ${
              key === selectedPill ? 'border-gray900' : 'border-gray500'
            } border  `}
          >
            <p className='text-sm leading-normal text-center text-dark-grey uppercase'>{title}</p>
          </div>
        );
      })}
    </HStack>
  );
}

export default MapViewSection;
